import { useCallback, useRef } from 'react';

import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useEvaluateLoyaltyServiceOffers } from 'features/offer/hooks/use-evaluate-loyalty-service-offers';
import { buildAppliedOfferServiceInputs } from 'features/offer/utils/build-applied-offer-service-inputs';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { LoyaltyPaymentMethod } from 'generated/graphql-gateway';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { getServiceModeFromLS } from 'state/service-mode';

/**
 * Hook to validate, against loyalty rules, the given cart entries
 */
export const useLoyaltyCartEntriesEvaluation = () => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const enableRewardCooldown = useFlag(LaunchDarklyFlag.ENABLE_REWARD_COOLDOWN);

  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const appliedLoyaltyOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { evaluateLoyaltyUserOffers, evaluateLoyaltyUserRewards } = useLoyaltyContext();

  // When the loyalty-service offers gate is ON, applied cart offers must be
  // validated against the loyalty service (mirroring `useCartIncentivesEvaluation`),
  // not the legacy `LoyaltyUserOffers` engine query.
  const { enabled: loyaltyServiceOffersEnabled } = useEnableLoyaltyServiceOffers();
  const { evaluateAndApplyOffersFeedback } = useEvaluateLoyaltyServiceOffers();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const dispatch = useAppDispatch();

  // Only the latest evaluation may clear `offersEvaluating`; a stale run
  // finishing must not re-enable "Continue" while a newer one is in flight.
  const latestOfferEvaluationRef = useRef(0);

  const evaluateLoyaltyCartEntries = useCallback(
    async (
      loyaltyId: string,
      cartEntries?: ILegacyCartEntry[],
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const promises: Promise<void>[] = [];

      if (appliedLoyaltyOffers.length) {
        // Gate "Continue" while validating. The ON (loyalty-service) path never
        // feeds the derived `offersLoading`, so bracket both paths with this flag.
        const offerEvaluationId = ++latestOfferEvaluationRef.current;
        dispatch(actions.loyalty.setOffersEvaluating(true));

        let offerEvaluation: Promise<void>;

        if (loyaltyServiceOffersEnabled) {
          // Mirror the legacy engine's incentive/cart-entry shapes so the service
          // feedback lands in the same Redux map the cart already reads. The
          // stale-guard + feedback dispatch live in `evaluateAndApplyOffersFeedback`.
          const { appliedIncentives, cartEntries: parsedEntries } = buildAppliedOfferServiceInputs({
            appliedOffers: appliedLoyaltyOffers,
            cartEntries,
            appliedRewards: appliedLoyaltyRewards,
          });

          offerEvaluation = evaluateAndApplyOffersFeedback({
            appliedIncentives,
            cartEntries: parsedEntries,
            serviceMode: getServiceModeFromLS(),
            storeId,
          });
        } else {
          offerEvaluation = evaluateLoyaltyUserOffers(
            loyaltyId,
            appliedLoyaltyOffers,
            cartEntries,
            appliedLoyaltyRewards,
            subtotalAmount,
            paymentMethod
          );
        }

        promises.push(
          offerEvaluation.finally(() => {
            if (offerEvaluationId === latestOfferEvaluationRef.current) {
              dispatch(actions.loyalty.setOffersEvaluating(false));
            }
          })
        );
      } else {
        // Release a gate set eagerly at price-start once all offers are removed.
        latestOfferEvaluationRef.current++;
        dispatch(actions.loyalty.setOffersEvaluating(false));
      }

      if (
        (enableRewardUnstacking || enableRewardCooldown) &&
        Object.values(appliedLoyaltyRewards).length
      ) {
        promises.push(
          evaluateLoyaltyUserRewards(
            loyaltyId,
            appliedLoyaltyRewards,
            cartEntries,
            appliedLoyaltyOffers,
            subtotalAmount,
            paymentMethod
          )
        );
      }

      await Promise.all(promises);
    },
    [
      appliedLoyaltyOffers,
      appliedLoyaltyRewards,
      loyaltyServiceOffersEnabled,
      evaluateAndApplyOffersFeedback,
      storeId,
      dispatch,
      evaluateLoyaltyUserOffers,
      evaluateLoyaltyUserRewards,
      enableRewardUnstacking,
      enableRewardCooldown,
    ]
  );

  return evaluateLoyaltyCartEntries;
};
